/**
 * documentDisplayDate — Documents date-column resolution.
 *
 * Server-side, a document's `effective_date` is COALESCE(authored_at,
 * created_at) where `authored_at` is a DATE-ONLY column — "the date on the
 * document" from Fast Card extraction or EHR migration. The coalesce
 * fabricates midnight UTC for authored rows, so rendering `effective_date`
 * through a clock+timezone formatter showed every authored/migrated document
 * as "8:00 PM the previous day" in US zones. Same resolution as the
 * Encounters GDN-2639 fix (`encounterDisplayTime`):
 *
 *   - `authored_at` present → the document's date is a PURE calendar date;
 *     render it with no clock and no timezone day-shift.
 *   - otherwise → `effective_date`/`created_at` is a genuine upload instant;
 *     render it timezone-aware.
 */

import { formatDateOnlyString, formatDateString } from '../../../utils/utils';

/** Structural subset of a document summary the date column needs. */
export interface DocumentDisplayDateLike {
  /** Date-only "YYYY-MM-DD" content date; null for plain uploads. */
  authored_at?: string | null;
  /** Server sort key: authored_at at midnight UTC, else created_at. */
  effective_date?: string | null;
  /** Upload instant (genuine UTC datetime). */
  created_at?: string | null;
}

export interface ResolvedDocumentDate {
  value: string;
  /** True when `value` names a day, not an instant — render it in UTC. */
  isDateOnly: boolean;
}

export const resolveDocumentDisplayDate = (
  document: DocumentDisplayDateLike,
): ResolvedDocumentDate | null => {
  if (document.authored_at) {
    return { value: document.authored_at, isDateOnly: true };
  }
  const instant = document.effective_date || document.created_at;
  return instant ? { value: instant, isDateOnly: false } : null;
};

/** Numeric label: "06/29/2026" (authored) or "06/28/2026, 03:14 PM" (upload). */
export const formatDocumentDisplayDate = (document: DocumentDisplayDateLike): string => {
  const resolved = resolveDocumentDisplayDate(document);
  if (!resolved) return '—';
  return resolved.isDateOnly
    ? formatDateOnlyString(resolved.value)
    : formatDateString(resolved.value);
};

const formatMedium = (value: string, isDateOnly: boolean): string =>
  new Date(value).toLocaleDateString(undefined, {
    ...(isDateOnly ? { timeZone: 'UTC' } : {}),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/** Medium label ("Jun 29, 2026") for the detail modal and documents grid. */
export const formatDocumentDisplayDateMedium = (
  document: DocumentDisplayDateLike,
): string | null => {
  const resolved = resolveDocumentDisplayDate(document);
  return resolved ? formatMedium(resolved.value, resolved.isDateOnly) : null;
};

/**
 * Medium-format a standalone date-only string ("YYYY-MM-DD") without a
 * timezone day-shift — e.g. a document's `signed_at`.
 */
export const formatDateOnlyMedium = (value?: string | null): string | null =>
  value ? formatMedium(value, true) : null;
